import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import Typography from "@material-ui/core/Typography";
import { LocalizerHelper } from "../../helpers/localizerHelper";
import { Button, Switch, TextField } from "@material-ui/core";
import { checkIfNotEmpty, checkValidEmail } from "../../helpers/validatorHelper";
import { DEFAULT_PRIVACY_URL, DEFAULT_TERMS_AND_CONDITION_URL, DELIVERY_MODES } from "../../constants/data";
import backgroundOval from './background_oval.png';
import signinLogo from './signin_logo.png';
import './index.scss';
import { RootState } from "../../store";
import firebase, { auth, remoteConfig } from "../../firebase";
import FacebookIcon from "@material-ui/icons/Facebook";
import EmailIcon from "@material-ui/icons/Email";
import { switchSessionToUser } from "../../features/session/session.service";
import { LoaderSpinner } from "../loader";
import { updateUserNickname, upgradeAnonymous } from "../../features/auth/auth.service";
import { Close } from "@material-ui/icons";
import { useEffect, useState } from "react";

const Signin = () => {
  const navigate = useNavigate();
  const { link_alias, restaurant: restaurantId, menu: menuId, dits: sessionId } = useParams<{ restaurant: string; menu: string, link_alias: string, dits: string }>();
  const lastLinkAlias = window.localStorage?.getItem('chuzeat_last_alias_link') ?? '';

  const appConfig = JSON.parse(remoteConfig.getString('m_app_config') || '{}');
  const privacyPolicyUrl = appConfig?.privacy_policy_url ?? DEFAULT_PRIVACY_URL;
  const termsAndConditionUrl = appConfig?.terms_and_condition_url ?? DEFAULT_TERMS_AND_CONDITION_URL;

  const session = useSelector((state: RootState) => state.session.session);
  const firestoreUser = useSelector((state: RootState) => state.auth.firestoreUser);

  const [email, setUserEmail] = useState('');
  const [password, setUserPassword] = useState('');
  const [nickname, setUserNickname] = useState('');
  const [disableEmailAction, setDisableEmailAction] = useState(false);
  const [emailError, setEmailError] = useState({ email: !checkIfNotEmpty(email), password: !checkIfNotEmpty(password) });
  const [disableNicknameAction, setDisableNicknameAction] = useState(false);
  const [nicknameError, setNicknameError] = useState({ nickname: !checkIfNotEmpty(nickname) });

  type AuthStep = "select" | "privacy_policy" | "email_login" | "nickname" | "checkNickname" | "loading";
  type Provider = "google" | "facebook" | "email" | null;

  const [authStep, setAuthStep] = useState<AuthStep>("select");
  const [provider, setProvider] = useState<Provider>(null);
  const [privacyPolicy, setPrivacyPolicy] = useState<boolean>(false);

  // Detect mobile
  const isMobile = () => /Mobi|Android|iPhone/i.test(navigator.userAgent);

  useEffect(() => {
    const handleRedirectResult = async () => {
      const anonymousUid = sessionStorage.getItem("anonymousUid");
      console.log(`anonymousUid [${anonymousUid}]`);
      if(!anonymousUid) return;
      setAuthStep("loading");
      try {
        const result = await auth.getRedirectResult();
        console.log(`getRedirectResult [${JSON.stringify(result)}]`);

        if (!result || !result.user) return setAuthStep("select");

        const credential = result.credential;
        const user = result.user;

        // Upgrade anonymous user
        await upgradeAnonymous();
        setAuthStep("checkNickname");

      } catch (err: any) {
        console.error("Redirect error:", err);
        switch (err.code) {
          case "auth/credential-already-in-use":
            try {
              await auth.signInWithCredential(err.credential);
              await switchSessionToUser(anonymousUid);
              setAuthStep("checkNickname");
            } catch (e) {
              alert(e);
              setAuthStep("select");
            }
            break;

          case "auth/provider-already-linked":
            await switchSessionToUser(anonymousUid);
            setAuthStep("checkNickname");
            break;

          default:
            alert(err.message);
            setAuthStep("select");
        }
      }
    };

    handleRedirectResult();
  }, []);

  useEffect(() => {
    if (authStep === "checkNickname" && firestoreUser) {
      if (firestoreUser.nickname.length <= 0) setAuthStep("nickname");
      else _goOn();
    }
  }, [authStep, firestoreUser]);

  // This should only work for facebook and google
  const _linkWithProviderPopUp = async (provider: firebase.auth.AuthProvider) => {
    console.log(`LINK WITH PROVIDER ${provider.providerId}`);
    const user = auth.currentUser;
    if (!user) return;
    setAuthStep("loading");
    const anonymousUid = user.uid;
    try {
      await user.linkWithPopup(provider);
      await upgradeAnonymous();
      return setAuthStep("checkNickname");
    } catch (err: any) {
      console.log(`Error in linkWithPopup ${err?.code} ${err.message}`);
      const credential = err.credential;
      console.log(`credential ${credential}`);
      switch(err.code){
        case "auth/credential-already-in-use":
          const credential = err.credential;
          try{
            console.log("LOG TO NEW CREDENTIAL");
            await auth.signInWithCredential(credential);
            await switchSessionToUser(anonymousUid);
            return setAuthStep("checkNickname");
          }catch(e){
            alert(e);
            return setAuthStep("select");
          }
        case "auth/popup-closed-by-user":
          return setAuthStep("select");
        case "auth/provider-already-linked":
          await switchSessionToUser(anonymousUid);
          return setAuthStep("checkNickname");
        default:
          alert(err);
          return setAuthStep("select");
      }
    }
  }

    // This should only work for facebook and google
  const _linkWithProviderRedirect = async (provider: firebase.auth.AuthProvider) => {
    console.log(`LINK WITH PROVIDER ${provider.providerId}`);
    const user = auth.currentUser;
    if (!user) return;
    setAuthStep("loading");
    try {
      // Save anonymous UID to recover session after redirect
      sessionStorage.setItem("anonymousUid", user.uid);

      await user.linkWithRedirect(provider);
      //await auth.signInWithRedirect(provider);
      // DO NOT do anything after this → browser redirects away
    } catch (err: any) {
      console.error("Error in linkWithRedirect", err);
      alert(err.message);
      setAuthStep("select");
    }

  }

  const _linkWithCredential = async (credential: firebase.auth.AuthCredential) => {
    console.log(`LINK WITH Credential ${credential.signInMethod}`);
    const user = auth.currentUser;
    if (!user) return;
    setAuthStep("loading");
    const anonymousUid = user.uid;

    try {
      await user.linkWithCredential(credential);
      await upgradeAnonymous();
      setAuthStep("checkNickname");
    } catch (err: any) {
      console.log(`Error in linkWithCredential ${err?.code}`);
      switch (err.code) {
        case "auth/credential-already-in-use":
        case "auth/email-already-in-use":
          try {
            await auth.signInWithCredential(credential);
            await switchSessionToUser(anonymousUid);
            setAuthStep("checkNickname");
          } catch (e: any) {
            alert(e);
            setAuthStep("select");
          }
          break;

        case "auth/provider-already-linked":
          await switchSessionToUser(anonymousUid);
          setAuthStep("checkNickname");
          break;

        default:
          alert(err);
          setAuthStep("select");
      }
    }
  };

  const _setEmail = (value: string) => { setUserEmail(value); _validateEmailData({ currentEmail: value }); };
  const _setPassword = (value: string) => { setUserPassword(value); _validateEmailData({ currentPassword: value }); };
  const _setNickname = (value: string) => { setUserNickname(value); _validateNicknameData({ currentNickname: value }); };

  const _validateEmailData = ({ currentEmail, currentPassword }: { currentEmail?: string, currentPassword?: string }) => {
    const _error = { ...emailError };
    _error.email = !checkValidEmail(currentEmail ?? email);
    _error.password = !checkIfNotEmpty(currentPassword ?? password);
    setDisableEmailAction(_error.email || _error.password);
    setEmailError(_error);
  };

  const _validateNicknameData = ({ currentNickname }: { currentNickname?: string }) => {
    const _error = { ...nicknameError };
    _error.nickname = !checkIfNotEmpty(currentNickname ?? nickname);
    setDisableNicknameAction(_error.nickname);
    setNicknameError(_error);
  };

  const _signinWithEmail = async () => {
    const credential = firebase.auth.EmailAuthProvider.credential(email, password);
    await _linkWithCredential(credential);
  };

  const _updateNickname = async () => {
    const user = auth.currentUser;
    if (!user) return;
    await updateUserNickname(user.uid, nickname);
    _goOn();
  };

  const _goOn = () => {
    const isDelivery = session?.delivery_mode === DELIVERY_MODES.delivery;
    const requestUserData = session?.service_type?.request_user_data ?? false;
    const alias = link_alias ?? lastLinkAlias;

    if (isDelivery) {
      const url = requestUserData ?
        `/menu/${alias}/info-details/${restaurantId}/${sessionId}/${menuId}/delivery` :
        `/menu/${alias}/confirm-order/${restaurantId}/${sessionId}/${menuId}`;
      navigate(url, { replace: true });
    } else {
      const url = requestUserData ?
        `/menu/${alias}/info-details/${restaurantId}/${sessionId}/${menuId}/takeaway` :
        `/menu/${alias}/confirm-order/${restaurantId}/${sessionId}/${menuId}`;
      navigate(url, { replace: true });
    }
  };

  const _handleClose = () => {
    const alias = link_alias ?? lastLinkAlias;
    const url = `/menu/${alias}/order-recap/${restaurantId}/${sessionId}/${menuId}`
    navigate(url); 
  };

  const _goOnPrivacy = () => {
    auth.useDeviceLanguage();
    switch (provider) {
      case "email": setAuthStep("email_login"); break;
      case "facebook": 
      const fbProvider = new firebase.auth.FacebookAuthProvider();
      if(isMobile()){
        _linkWithProviderRedirect(fbProvider); 
      }else{
        fbProvider.setCustomParameters({
          'display': 'popup'
        });
        _linkWithProviderPopUp(fbProvider); 
      }
      break;
      case "google": _linkWithProviderPopUp(new firebase.auth.GoogleAuthProvider()); break;
      default: setAuthStep("select");
    }
  };

  return (
    <div className="container page-container signin-container">
      <div className="signin-container-background">
          <img className="signin-background" src={backgroundOval}/>
          <div className="row signin-logo-container">
              <div className="col-12 text-center">
                  <img className='signin-logo' src={signinLogo}/>
              </div>
          </div>
      </div>
      <div className="header-row text-right">
        <Button onClick={_handleClose} className="close-button">
          <Close htmlColor="#fff" />
        </Button>
      </div>
      <div className="signin-inner-container">
        <div className="row">
            <div className="col-12" style={{marginTop: 40}}>
                <Typography variant="h4" gutterBottom align="center" className="page-header">
                    {LocalizerHelper.localized('proceed_signin_needed')}
                </Typography>
            </div>
        </div>
        {authStep === "select" &&
          <div className="row">
            <div className="col-md-12">
              <div className="signin-buttons-container">
                <button
                  className="provider-btn"
                  onClick={() => {
                    setProvider("facebook");
                    setAuthStep("privacy_policy");
                  }}                >
                  <FacebookIcon className="provider-icon-facebook" />
                  {LocalizerHelper.localized("sign_in_with_facebook")}
                </button>

                <button
                  className="provider-btn"
                  onClick={() => {
                    setProvider("google");
                    setAuthStep("privacy_policy");
                  }}
                >
                  <img
                    src="https://developers.google.com/identity/images/g-logo.png"
                    alt="google"
                    className="provider-icon-google"
                  />
                  {LocalizerHelper.localized("sign_in_with_google")}

                </button>

                <button
                  className="provider-btn"
                  onClick={() => {
                    setProvider("email");
                    setAuthStep("privacy_policy");
                  }}
                >
                  <EmailIcon className="provider-icon-email" />
                  {LocalizerHelper.localized("sign_in_with_email")}
                </button>

              </div>
            </div>
          </div>
        }
        {authStep === "privacy_policy" && 
          <div className="privacy-policy-container">
              <div className="row" style={{marginTop: 10}}>
                  <div className="col-md-12 privacy-policy-row">
                      {LocalizerHelper.localized('accept_privacy_policy')} 
                      <a target="_blank" href={privacyPolicyUrl} className="privacy-policy-link">{LocalizerHelper.localized('privacy_policy_info')}</a>
                      <Switch checked={privacyPolicy} onChange={()=> setPrivacyPolicy(!privacyPolicy)}></Switch>
                  </div>
              </div>
              <div className="row" style={{marginTop: 10, marginBottom: 10}}>
                  <div className="col-md-12 privacy-policy-row">
                      {LocalizerHelper.localized('accept_terms_and_conditions')} 
                      <a target="_blank" href={termsAndConditionUrl} className="privacy-policy-link"> {LocalizerHelper.localized('terms_and_conditions_info')}</a>
                  </div>
              </div>
              <Button 
                className="accent-button" 
                onClick={() => {_goOnPrivacy()}}
                disabled={!privacyPolicy}
              >
                  {LocalizerHelper.localized('proceed_with_order')}
              </Button>
          </div>
        }
        {authStep === "email_login" &&
          <div className="profile-form-container">
            <div className="row" style={{marginTop: 10}}>
              <div className="col-md-12">
                <TextField
                  variant="outlined"
                  name="email"
                  value={email}
                  error={emailError.email}
                  fullWidth={true}
                  helperText={LocalizerHelper.localized('field_required')}
                  label={LocalizerHelper.localized('email')}
                  onChange={(event) => _setEmail(event.target.value)}>
                </TextField>
              </div>
            </div>
            <div className="row" style={{marginTop: 10}}>
              <div className="col-md-12">
                <TextField
                  variant="outlined"
                  name="password"
                  type="password"
                  value={password}
                  error={emailError.password}
                  fullWidth={true}
                  helperText={LocalizerHelper.localized('field_required')}
                  label={LocalizerHelper.localized('password')}
                  onChange={(e) => _setPassword(e.target.value)}
                />
              </div>
            </div>
          </div>
        }
        {authStep === "nickname" &&
          <div className="profile-form-container">
            <div className="row" style={{marginTop: 10}}>
              <div className="col-md-12">
                <TextField
                  variant="outlined"
                  name="nickname"
                  value={nickname}
                  error={nicknameError.nickname}
                  helperText={LocalizerHelper.localized('field_required')}
                  fullWidth={true}
                  label={LocalizerHelper.localized('nickname')}
                  onChange={(event) => _setNickname(event.target.value)}>
                </TextField>
              </div>
            </div>
          </div>
        }
        {authStep === "email_login" &&
            <div className="profile-form-container bottom-fixed-row signin-bottom-fixed-row">
                <div className="row">
                    <div className="col-md-12">
                        <Button className='accent-button full-width' disabled={disableEmailAction}
                                onClick={() => _signinWithEmail()}>
                            {LocalizerHelper.localized('confirm')}
                        </Button>
                    </div>
                </div>
            </div>
        }
        {authStep === "nickname" &&
            <div className="profile-form-container bottom-fixed-row signin-bottom-fixed-row">
                <div className="row">
                    <div className="col-md-12">
                        <Button className='accent-button full-width' disabled={disableNicknameAction}
                                onClick={() => _updateNickname()}>
                            {LocalizerHelper.localized('confirm')}
                        </Button>
                    </div>
                </div>
            </div>
        }
        {(authStep === "loading" || authStep === "checkNickname") && 
          <LoaderSpinner/>
          //<LoaderGif image="/assets/loading" message={LocalizerHelper.localized('loading_msg_check_payment')} />
        }
      </div>
    </div>
  );
};

export default Signin;